/**
 * Helpers for wiring uploaded custom fonts into the GrapesJS editor:
 *  - inject @font-face rules so fonts render in the canvas (and the React UI)
 *  - add each font as an option in the Style Manager font-family selector
 *
 * The published page gets its own @font-face block injected server-side
 * (see server/blobStorage.js), so these helpers only affect the editing UI.
 */

const CANVAS_STYLE_ID = 'custom-fonts-faces';
const DOC_STYLE_ID = 'custom-fonts-faces-doc';

function fontFormatForUrl(url) {
  const ext = (String(url || '').match(/\.([a-z0-9]+)(?:\?|#|$)/i) || [])[1] || '';
  switch (ext.toLowerCase()) {
    case 'woff2':
      return 'woff2';
    case 'woff':
      return 'woff';
    case 'ttf':
      return 'truetype';
    case 'otf':
      return 'opentype';
    case 'eot':
      return 'embedded-opentype';
    default:
      return '';
  }
}

/** Build the CSS @font-face text for a list of fonts. */
export function buildFontFaceCss(fonts) {
  if (!Array.isArray(fonts)) return '';
  return fonts
    .filter((f) => f && f.url && f.family)
    .map((f) => {
      const family = String(f.family).replace(/["\\]/g, '');
      const format = f.format || fontFormatForUrl(f.url);
      const srcFormat = format ? ` format('${format}')` : '';
      const weight = f.weight ? `\n  font-weight: ${f.weight};` : '';
      const style = f.style ? `\n  font-style: ${f.style};` : '';
      return `@font-face {\n  font-family: '${family}';\n  src: url('${f.url}')${srcFormat};${weight}${style}\n  font-display: swap;\n}`;
    })
    .join('\n');
}

function upsertStyleEl(doc, id, css) {
  if (!doc) return;
  let el = doc.getElementById(id);
  if (!el) {
    el = doc.createElement('style');
    el.id = id;
    (doc.head || doc.documentElement || doc.body)?.appendChild(el);
  }
  el.textContent = css;
}

/** Inject @font-face rules into the editor canvas iframe and the host document. */
export function injectFontFaces(editor, fonts) {
  const css = buildFontFaceCss(fonts);
  try {
    const canvasDoc = editor?.Canvas?.getDocument?.();
    if (canvasDoc) upsertStyleEl(canvasDoc, CANVAS_STYLE_ID, css);
  } catch (e) {
    /* canvas may not be ready yet */
  }
  if (typeof document !== 'undefined') {
    upsertStyleEl(document, DOC_STYLE_ID, css);
  }
}

/** Find the Style Manager font-family property across all sectors. */
function findFontFamilyProperty(editor) {
  const sm = editor?.StyleManager;
  if (!sm || typeof sm.getSectors !== 'function') return null;
  const sectors = sm.getSectors();
  const sectorList = Array.isArray(sectors) ? sectors : sectors?.models || [];
  for (const sector of sectorList) {
    const props = typeof sector.getProperties === 'function' ? sector.getProperties() : [];
    const propList = Array.isArray(props) ? props : props?.models || [];
    const found = propList.find((p) => {
      const propName = p.getName ? p.getName() : p.get?.('name');
      const cssProp = p.get ? p.get('property') : null;
      return propName === 'font-family' || cssProp === 'font-family';
    });
    if (found) return found;
  }
  return null;
}

/** Add each custom font as an option in the font-family selector (deduped by value). */
export function registerFontFamilyOptions(editor, fonts) {
  if (!Array.isArray(fonts) || fonts.length === 0) return;
  const prop = findFontFamilyProperty(editor);
  if (!prop || typeof prop.getOptions !== 'function') return;

  const existing = prop.getOptions() || [];
  const valueOf = (o) =>
    typeof prop.getOptionId === 'function' ? prop.getOptionId(o) : o.value || o.id || o.name;
  const existingValues = new Set(existing.map((o) => String(valueOf(o)).toLowerCase()));

  const additions = [];
  fonts.forEach((f) => {
    if (!f || !f.family) return;
    const family = String(f.family).replace(/["\\]/g, '');
    const value = `"${family}", sans-serif`;
    if (existingValues.has(value.toLowerCase())) return;
    existingValues.add(value.toLowerCase());
    additions.push({ id: value, value, name: family });
  });

  if (additions.length === 0) return;
  try {
    prop.setOptions([...existing, ...additions]);
  } catch (e) {
    /* ignore option registration failures */
  }
}

/** Apply custom fonts to both the canvas preview and the font-family selector. */
export function applyCustomFonts(editor, fonts) {
  injectFontFaces(editor, fonts);
  registerFontFamilyOptions(editor, fonts);
}
